import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Play, Calendar, Timer, Trash2, ArrowRight, Coffee, Target, ListOrdered } from "lucide-react";

const DAYS_SHORT = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
const DAYS_FULL = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

export default function Program() {
  const [active, setActive] = useState(undefined); // undefined loading, null none
  const [programs, setPrograms] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [weeks, setWeeks] = useState(4);
  const navigate = useNavigate();

  const load = async () => {
    const [a, list] = await Promise.all([
      api.get("/programs/active").then((r) => r.data).catch(() => null),
      api.get("/programs").then((r) => r.data).catch(() => []),
    ]);
    setActive(a);
    setPrograms(list);
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    toast.info("Coach al lavoro… genero il programma personalizzato");
    try {
      const r = await api.post("/coach/plan", { weeks });
      toast.success(`Programma "${r.data.title}" creato`);
      setActive(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione programma");
    } finally {
      setGenerating(false);
    }
  };

  const delProgram = async (id) => {
    if (!window.confirm("Eliminare questo programma e tutte le sue sessioni?")) return;
    try {
      await api.delete(`/programs/${id}`);
      toast.success("Programma eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const activate = async (id) => {
    try {
      await api.patch(`/programs/${id}/activate`);
      toast.success("Programma attivato");
      load();
    } catch { toast.error("Errore"); }
  };

  if (active === undefined) return <div className="max-w-6xl mx-auto p-8 text-[#A0A0AB]">Caricamento...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-2">Piano personalizzato</div>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.9] mb-8">
        Programma <span className="text-[#FF3B30]">settimanale</span>
      </h1>

      {!active ? (
        <div className="border border-[#2A2A35] p-8 mb-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#E2FF31] mb-3">
              <Sparkles className="w-3 h-3" /> Nessun programma attivo
            </div>
            <div className="font-display text-3xl font-bold uppercase leading-tight mb-3">
              Fai generare al Coach IA un programma su misura per te.
            </div>
            <div className="text-[#A0A0AB] mb-6 text-sm">
              Il piano viene costruito in base ai dati del tuo <Link to="/profile" className="text-[#FF3B30] underline">profilo</Link>:
              obiettivi, livello, attrezzatura, giorni disponibili, eventuali infortuni. Assicurati di averlo compilato.
            </div>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Durata programma</div>
                <div className="flex gap-2">
                  {[2, 4, 6, 8].map((w) => (
                    <button
                      key={w}
                      onClick={() => setWeeks(w)}
                      data-testid={`weeks-${w}`}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-all ${
                        weeks === w ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#2A2A35] text-[#A0A0AB] hover:border-white"
                      }`}
                    >{w} sett</button>
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={generate}
              disabled={generating}
              data-testid="generate-plan-btn"
              className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#e02e24] transition-all flex items-center gap-2 disabled:opacity-40"
            >
              <Sparkles className="w-4 h-4" /> {generating ? "Generazione…" : "Genera con IA"}
            </button>
          </div>
        </div>
      ) : (
        <ActiveProgram program={active} navigate={navigate} onRegen={generate} regenerating={generating} />
      )}

      {/* History */}
      {programs.length > 1 && (
        <div className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-3">Cronologia programmi</div>
          <div className="border border-[#2A2A35] divide-y divide-[#2A2A35]">
            {programs.filter((p) => !active || p.id !== active.id).map((p) => (
              <div key={p.id} className="p-4 flex items-center justify-between gap-4" data-testid={`prg-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-bold uppercase truncate">{p.title}</div>
                  <div className="text-xs text-[#A0A0AB]">
                    {new Date(p.created_at).toLocaleDateString("it-IT")} · {p.sessions_per_week} sess/sett · {p.weeks} sett
                  </div>
                </div>
                <button onClick={() => activate(p.id)} className="border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] text-[10px] font-bold uppercase tracking-widest px-3 py-2" data-testid={`activate-${p.id}`}>Attiva</button>
                <button onClick={() => delProgram(p.id)} className="border border-[#2A2A35] hover:border-[#EF4444] hover:text-[#EF4444] p-2" data-testid={`del-prg-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActiveProgram({ program, navigate, onRegen, regenerating }) {
  const byDay = {};
  for (const w of program.workouts || []) byDay[w.day_of_week] = w;

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#E2FF31] flex items-center gap-1">
            <Sparkles className="w-3 h-3" />Attivo
          </span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">
            {program.sessions_per_week} sessioni/sett · {program.weeks} settimane
          </span>
        </div>
        <div className="font-display text-3xl font-bold uppercase leading-tight">{program.title}</div>
        {program.summary && <div className="text-sm text-[#A0A0AB] mt-2 max-w-3xl">{program.summary}</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3 mb-6 stagger">
        {[1, 2, 3, 4, 5, 6, 7].map((d) => {
          const w = byDay[d];
          return (
            <div
              key={d}
              className={`border p-4 min-h-[180px] flex flex-col ${
                w ? "border-[#2A2A35] hover:border-[#FF3B30] transition-all" : "border-[#2A2A35] bg-[#141419]/50"
              }`}
              data-testid={`day-${d}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mb-2">{DAYS_SHORT[d - 1]}</div>
              {w ? (
                <>
                  <div className="font-display text-lg font-bold uppercase leading-tight mb-2 flex-1">{w.title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#E2FF31] mb-1 flex items-center gap-1">
                    <Target className="w-3 h-3" />{w.focus}
                  </div>
                  <div className="text-xs text-[#A0A0AB] flex items-center gap-2 mb-3">
                    <Timer className="w-3 h-3" />{w.duration_min}min · {w.exercises.length} es.
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => navigate(`/session/${w.id}`)}
                      data-testid={`start-day-${d}`}
                      className="flex-1 bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-widest py-2 hover:bg-[#e02e24] transition-all flex items-center justify-center gap-1"
                    >
                      <Play className="w-3 h-3 fill-white" /> Inizia
                    </button>
                    <button
                      onClick={() => navigate(`/workouts/${w.id}`)}
                      data-testid={`detail-day-${d}`}
                      className="border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] p-2 transition-all"
                      aria-label="Vedi ordine esercizi"
                      title="Ordine esercizi"
                    >
                      <ListOrdered className="w-3 h-3" />
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <Coffee className="w-6 h-6 text-[#A0A0AB]/50 mb-2" />
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">Riposo</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3">
        <Link
          to="/workouts"
          className="border border-[#2A2A35] hover:border-white text-white font-bold uppercase tracking-widest text-xs px-4 py-2 flex items-center gap-1"
          data-testid="see-all-sessions"
        >
          Tutte le sessioni <ArrowRight className="w-3 h-3" />
        </Link>
        <button
          onClick={onRegen}
          disabled={regenerating}
          data-testid="regen-plan"
          className="border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] text-xs font-bold uppercase tracking-widest px-4 py-2 flex items-center gap-1 disabled:opacity-40"
        >
          <Sparkles className="w-3 h-3" /> {regenerating ? "..." : "Rigenera programma"}
        </button>
      </div>
    </>
  );
}
